/**
 * ShareDialog - Dialog for managing entity access/sharing.
 *
 * Displays current access list and allows adding/removing access.
 */
import { useState } from 'react';
import {
  User as UserIcon,
  Users,
  Building2,
  Shield,
  Trash2,
  Link as LinkIcon,
  Loader2,
  ArrowLeftRight,
  ChevronsUpDown } from 'lucide-react';
import { copyToClipboard } from '../../utils/clipboard';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { Separator } from '../../../components/ui/separator';
import { Label } from '../../../components/ui/label';
import { BidiText } from '../common/BidiText';
import { LtrToken } from '../common/LtrToken';
import { ResponsiveDialog } from '../common/ResponsiveDialog';
import { EntityLoadState, ConfirmDialog, UserPicker } from '../common';
import { useAuth } from '../../auth';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList } from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger } from '../../../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from '../../../components/ui/select';
import { toast } from 'sonner';
import axiosInstance from '../../api/axiosInstance';
import { API_BASE_URL } from '../../api/config';
import {
  useAdminWriteElevation,
  useEntityAccess,
  useGrantAccess,
  usePendingAccessRequests,
  useRevokeAccess,
  useTransferOwnership } from './usePermissions';
import type { RelationType, SubjectType, AccessEntry } from './types';
import { RELATION_HIERARCHY } from './types';
import { ManagerVisibilityInfo } from './ManagerVisibilityInfo';

interface User {
  id: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  email: string;
  profile_image_url?: string;
}

interface Group {
  id: string;
  name: string;
  description?: string;
}

// Common type for autocomplete options
type SearchOption = User | Group;

function isUser(option: SearchOption): option is User {
  return 'email' in option;
}

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  /** Entity type in format module.entity (e.g., 'tasks.board') */
  objectType: string;
  /** Entity UUID */
  objectId: string;
  /** Display name for the entity (shown in dialog title) */
  entityName?: string;
  /** Relations that can be granted (defaults to all) */
  allowedRelations?: RelationType[];
  /** Whether user can share (has owner/share permission) */
  canShare?: boolean;
}

export function ShareDialog({
  open,
  onClose,
  objectType,
  objectId,
  entityName,
  allowedRelations = [...RELATION_HIERARCHY],
  canShare = true }: ShareDialogProps) {
  const { t } = useTranslation('common');
  const { user: currentUser } = useAuth();
  const [searchType, setSearchType] = useState<'user' | 'group'>('user');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubject, setSelectedSubject] = useState<SearchOption | null>(null);
  const [selectedRelation, setSelectedRelation] = useState<RelationType>('viewer');
  const [searchOpen, setSearchOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTargetId, setTransferTargetId] = useState<string | null>(null);

  // Fetch current access list
  const {
    data: accessData,
    isLoading: isLoadingAccess,
    error: accessError } = useEntityAccess({
    objectType,
    objectId,
    enabled: open });

  // Mutations
  const grantAccess = useGrantAccess();
  const revokeAccess = useRevokeAccess();
  const transferOwnership = useTransferOwnership();

  // Search users (exclude external users — they cannot be sharing targets)
  const { data: users = [], isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users', 'search', searchQuery],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      params.append('exclude_user_type', 'external');
      params.append('limit', '20');
      const { data } = await axiosInstance.get(`/api/v1/users?${params}`);
      if (Array.isArray(data?.data)) return data.data as User[];
      if (Array.isArray(data)) return data as User[];
      return [];
    },
    enabled: open && searchType === 'user' && searchQuery.length >= 1 });

  // Search groups
  const { data: groups = [], isLoading: isLoadingGroups } = useQuery({
    queryKey: ['groups', 'search', searchQuery],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      params.append('limit', '20');
      const { data } = await axiosInstance.get(`/api/v1/groups?${params}`);
      if (Array.isArray(data?.data)) return data.data as Group[];
      if (Array.isArray(data)) return data as Group[];
      return [];
    },
    enabled: open && searchType === 'group' && searchQuery.length >= 1 });

  const getDisplayName = (user: User) =>
    user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;

  const getAvatarUrl = (user: User) =>
    user.profile_image_url ? `${API_BASE_URL}${user.profile_image_url}` : undefined;

  const handleGrant = async () => {
    if (!selectedSubject) return;

    const subjectType: SubjectType = searchType;
    await grantAccess.mutateAsync({
      subjectType,
      subjectId: selectedSubject.id,
      relation: selectedRelation,
      objectType,
      objectId });

    // Reset form
    setSelectedSubject(null);
    setSearchQuery('');
    toast.success(t('sharing.accessGranted'));
  };

  const handleRevoke = async (entry: AccessEntry) => {
    await revokeAccess.mutateAsync({
      subjectType: entry.subject.type,
      subjectId: entry.subject.id,
      objectType,
      objectId });
    toast.success(t('sharing.accessRevoked'));
  };

  const handleChangeRelation = async (entry: AccessEntry, newRelation: RelationType) => {
    if (newRelation === entry.relation) return;

    await grantAccess.mutateAsync({
      subjectType: entry.subject.type,
      subjectId: entry.subject.id,
      relation: newRelation,
      objectType,
      objectId });
    toast.success(t('sharing.accessChanged'));
  };

  // Granting from the request list reuses the ordinary share path, so the
  // grant route's own `settle_granted` closes the ask — there is no second
  // "approve" endpoint that could disagree with an ordinary share.
  const handleGrantToRequester = async (userId: string, relation: RelationType) => {
    await grantAccess.mutateAsync({
      subjectType: 'user',
      subjectId: userId,
      relation,
      objectType,
      objectId });
    toast.success(t('sharing.accessGranted'));
  };

  const handleCopyLink = () => {
    const url = window.location.href;
    copyToClipboard(url);
    toast.success(t('sharing.linkCopied'));
  };

  const getSubjectIcon = (type: SubjectType) => {
    switch (type) {
      case 'user':
        return <UserIcon className="h-4 w-4" />;
      case 'group':
        return <Users className="h-4 w-4" />;
      case 'company':
        return <Building2 className="h-4 w-4" />;
      case 'role':
        return <Shield className="h-4 w-4" />;
      default:
        return <UserIcon className="h-4 w-4" />;
    }
  };

  const getSourceLabel = (source: string): string => {
    return t(`sharing.sources.${source}`, source);
  };

  const getSubjectTypeLabel = (type: SubjectType): string => {
    return t(`sharing.subjectTypes.${type}`, type);
  };

  // Sort relations by hierarchy (most permissive first)
  const sortedRelations = [...allowedRelations].sort(
    (a, b) => RELATION_HIERARCHY.indexOf(b) - RELATION_HIERARCHY.indexOf(a)
  );

  // Extract owner entry from access data (for manager visibility + transfer affordance)
  const ownerEntry = accessData?.access?.find(
    (entry) => entry.relation === 'owner' && entry.subject.type === 'user'
  );
  const ownerId = ownerEntry?.subject.id;

  // Transfer-ownership affordance (design §4.1):
  //  - the current owner can always transfer their own record;
  //  - a tenant admin can additionally RECOVER a record whose owner is non-active
  //    (inactive/locked/soft-deleted) or missing;
  //  - while the admin holds an ACTIVE §4.1 write elevation on THIS record
  //    (WI a73e09ac), the forced transfer from an active owner is also allowed
  //    (departing-employee path — the elevation was step-up-gated + audited).
  // The backend enforces all three; this only decides visibility.
  const adminElevation = useAdminWriteElevation(objectType, objectId);
  const currentUserIsOwner = !!ownerId && ownerId === currentUser?.id;
  // Pending "ask the owner for access" requests (WI 656e6e1b). Gated on the
  // caller BEING the owner, so a non-owner's ShareDialog costs no extra request
  // — the backend scopes it the same way, and returns [] for everyone else.
  const pendingRequests = usePendingAccessRequests(
    objectType,
    objectId,
    open && currentUserIsOwner,
  );
  const hasActiveOwner = accessData?.access?.some(
    (entry) =>
      entry.relation === 'owner' &&
      entry.subject.type === 'user' &&
      entry.subject.is_active === true
  );
  const canTransferOwnership =
    !!accessData &&
    (currentUserIsOwner ||
      (!!currentUser?.is_admin && (!hasActiveOwner || adminElevation.active)));

  const handleTransferOwnership = async () => {
    if (!transferTargetId) return;
    await transferOwnership.mutateAsync({
      toUserId: transferTargetId,
      objectType,
      objectId });
    setTransferOpen(false);
    setTransferTargetId(null);
    toast.success(t('sharing.ownershipTransferred'));
  };

  // Extract module from objectType (e.g., 'sales.deal' → 'sales')
  const module = objectType.includes('.') ? objectType.split('.')[0] : objectType;

  const searchOptions = (searchType === 'user' ? users : groups) as SearchOption[];
  const isSearchLoading = searchType === 'user' ? isLoadingUsers : isLoadingGroups;

  // The record's own name is Class A (unknown-language display text) inside our
  // translated title, so it carries its own bidi isolate via the key's
  // `<entity>` slot — otherwise a Hebrew title reorders on a name opening with a
  // Latin token. `ResponsiveDialog['title']` is a ReactNode, so the `<Trans>`
  // reaches `DialogTitle`/`SheetTitle` unchanged.
  const dialogTitle = entityName ? (
    <Trans
      t={t}
      i18nKey="sharing.shareEntity"
      values={{ name: entityName }}
      components={{ entity: <BidiText /> }}
    />
  ) : (
    t('sharing.share')
  );

  return (
    <>
    <ResponsiveDialog
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      title={dialogTitle}
      className="max-w-md"
    >
      <div className="space-y-4">
        {/* Add people/groups section */}
        {canShare && (
          <div className="space-y-3">
            <p className="text-sm font-medium">{t('sharing.addPeopleOrGroups')}</p>
            <div className="flex gap-2">
              <Badge
                variant={searchType === 'user' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => {
                  setSearchType('user');
                  setSelectedSubject(null);
                }}
              >
                <UserIcon className="h-3 w-3 me-1" />
                {t('sharing.users')}
              </Badge>
              <Badge
                variant={searchType === 'group' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => {
                  setSearchType('group');
                  setSelectedSubject(null);
                }}
              >
                <Users className="h-3 w-3 me-1" />
                {t('sharing.groups')}
              </Badge>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              {/* Subject search */}
              <Popover open={searchOpen} onOpenChange={setSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={searchOpen}
                    className="w-full sm:flex-1 justify-between font-normal"
                  >
                    {selectedSubject ? (
                      isUser(selectedSubject) ? getDisplayName(selectedSubject) : selectedSubject.name
                    ) : (
                      <span className="text-muted-foreground">
                        {searchType === 'user'
                          ? t('sharing.searchUsers')
                          : t('sharing.searchGroups')}
                      </span>
                    )}
                    <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[min(300px,calc(100vw-3rem))] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder={
                        searchType === 'user'
                          ? t('sharing.searchUsers')
                          : t('sharing.searchGroups')
                      }
                      value={searchQuery}
                      onValueChange={setSearchQuery}
                    />
                    <CommandList>
                      {isSearchLoading && (
                        <div className="py-6 text-center">
                          <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                        </div>
                      )}
                      {!isSearchLoading && searchOptions.length === 0 && (
                        <CommandEmpty>{t('app.noResults')}</CommandEmpty>
                      )}
                      {!isSearchLoading && searchOptions.length > 0 && (
                        <CommandGroup>
                          {searchOptions.map((option) => (
                            <CommandItem
                              key={option.id}
                              value={option.id}
                              onSelect={() => {
                                setSelectedSubject(option);
                                setSearchOpen(false);
                              }}
                            >
                              {isUser(option) ? (
                                <div className="flex items-center gap-2 w-full">
                                  <Avatar className="h-6 w-6">
                                    <AvatarImage src={getAvatarUrl(option)} />
                                    <AvatarFallback className="text-xs">
                                      {getDisplayName(option).charAt(0)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm truncate">{getDisplayName(option)}</p>
                                    <p className="text-xs text-muted-foreground truncate">
                                      {option.email}
                                    </p>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 w-full">
                                  <Avatar className="h-6 w-6">
                                    <AvatarFallback className="text-xs">
                                      <Users className="h-3 w-3" />
                                    </AvatarFallback>
                                  </Avatar>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm truncate">{option.name}</p>
                                    {option.description && (
                                      <p className="text-xs text-muted-foreground truncate">
                                        {option.description}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {/* Relation select + Add button */}
              <div className="flex gap-2">
                <Select
                  value={selectedRelation}
                  onValueChange={(val) => setSelectedRelation(val as RelationType)}
                >
                  <SelectTrigger className="flex-1 sm:w-[110px] sm:flex-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sortedRelations.map((relation) => (
                      <SelectItem key={relation} value={relation}>
                        {t(`sharing.relations.${relation}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Add button */}
                <Button
                  onClick={handleGrant}
                  disabled={!selectedSubject || grantAccess.isPending}
                  className="shrink-0"
                >
                  {grantAccess.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    t('app.add')
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        <Separator />

        {/* Current access list */}
        <div className="space-y-2">
          <p className="text-sm font-medium">{t('sharing.whoHasAccess')}</p>

          {isLoadingAccess && (
            <div className="py-6 text-center">
              <Loader2 className="h-5 w-5 animate-spin mx-auto" />
            </div>
          )}

          {accessError && <EntityLoadState error={accessError} />}

          {!isLoadingAccess && accessData && (
            <div className="space-y-1">
              {accessData.access.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  {t('sharing.noAccessYet')}
                </p>
              ) : (
                accessData.access.map((entry, index) => (
                  <div
                    key={`${entry.subject.type}-${entry.subject.id}-${index}`}
                    className="flex items-center gap-2 py-2"
                  >
                    <Avatar className="h-8 w-8">
                      {entry.subject.avatar_url && (
                        <AvatarImage src={`${API_BASE_URL}${entry.subject.avatar_url}`} />
                      )}
                      <AvatarFallback>{getSubjectIcon(entry.subject.type)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">
                        {entry.subject.name || entry.subject.id}
                      </p>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-muted-foreground truncate">
                          {entry.subject.email || getSubjectTypeLabel(entry.subject.type)}
                        </span>
                        {entry.source !== 'direct' && (
                          <Badge variant="outline" className="text-[10px] h-4">
                            {getSourceLabel(entry.source)}
                          </Badge>
                        )}
                      </div>
                    </div>
                    {canShare && entry.source === 'direct' ? (
                      <div className="flex items-center gap-1 shrink-0">
                        <Select
                          value={entry.relation}
                          onValueChange={(val) =>
                            handleChangeRelation(entry, val as RelationType)
                          }
                          disabled={grantAccess.isPending}
                        >
                          <SelectTrigger className="w-[85px] sm:w-[100px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {sortedRelations.map((relation) => (
                              <SelectItem key={relation} value={relation} className="text-xs">
                                {t(`sharing.relations.${relation}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={() => handleRevoke(entry)}
                          disabled={revokeAccess.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <Badge
                        variant={entry.relation === 'owner' ? 'default' : 'secondary'}
                      >
                        {t(`sharing.relations.${entry.relation}`)}
                      </Badge>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Inherited access from parent (e.g., workspace) */}
        {!isLoadingAccess && accessData?.inherited_access && accessData.inherited_access.length > 0 && (
          <>
            <Separator />
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-muted-foreground">
                  {/* The parent's name is Class A too — same `<entity>` slot as
                      the dialog title above. */}
                  {accessData.parent_entity?.name ? (
                    <Trans
                      t={t}
                      i18nKey="sharing.inheritedAccessFrom"
                      values={{ name: accessData.parent_entity.name }}
                      components={{ entity: <BidiText /> }}
                    />
                  ) : (
                    t('sharing.inheritedAccess')
                  )}
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('sharing.inheritedAccessDescription')}
              </p>
              <div className="space-y-1 opacity-75">
                {accessData.inherited_access.map((entry, index) => (
                  <div
                    key={`inherited-${entry.subject.type}-${entry.subject.id}-${index}`}
                    className="flex items-center gap-2 py-2"
                  >
                    <Avatar className="h-8 w-8">
                      {entry.subject.avatar_url && (
                        <AvatarImage src={`${API_BASE_URL}${entry.subject.avatar_url}`} />
                      )}
                      <AvatarFallback>{getSubjectIcon(entry.subject.type)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">
                        {entry.subject.name || entry.subject.id}
                      </p>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-muted-foreground truncate">
                          {entry.subject.email || getSubjectTypeLabel(entry.subject.type)}
                        </span>
                        <Badge variant="outline" className="text-[10px] h-4">
                          {entry.source_detail || getSourceLabel(entry.source)}
                        </Badge>
                      </div>
                    </div>
                    <Badge variant="secondary">
                      {t(`sharing.relations.${entry.relation}`)}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Pending access requests (WI 656e6e1b) — only the OWNER sees this, and
            the backend scopes it the same way, so it can never name a requester
            on a record this caller could not act on. Granting reuses the ordinary
            share path above, which settles the ask server-side; declining is the
            only action that needs its own control. */}
        {pendingRequests.requests.length > 0 && (
          <>
            <Separator />
            <div className="space-y-2">
              <div>
                <Label>{t('sharing.accessRequestsTitle')}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('sharing.accessRequestsDescription')}
                </p>
              </div>
              <div className="space-y-2">
                {pendingRequests.requests.map((request) => (
                  <div
                    key={request.id}
                    className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2"
                  >
                    <div className="min-w-0 flex-1">
                      {/* Two DIFFERENT bidi classes, so the two arms differ: a
                          person's NAME is Class A (unknown language — BidiText
                          resolves it by word majority), while an EMAIL is a Class B
                          digit/separator token and is pinned LTR. BidiText's
                          word-majority heuristic has no strong character to latch
                          onto for a digits-only local part; LtrToken is what this
                          batch's own sibling uses for an email (ViewOnlyBanner). */}
                      <p className="truncate text-sm font-medium">
                        {request.requester_name ? (
                          <BidiText>{request.requester_name}</BidiText>
                        ) : (
                          <LtrToken>{request.requester_email}</LtrToken>
                        )}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {t('sharing.accessRequestAsked', {
                          relation: t(`sharing.relations.${request.requested_relation}`),
                        })}
                      </p>
                      {request.message && (
                        <p className="truncate text-xs text-muted-foreground">
                          <BidiText>{request.message}</BidiText>
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Button
                        size="sm"
                        disabled={grantAccess.isPending}
                        onClick={() =>
                          void handleGrantToRequester(
                            request.requester_user_id,
                            request.requested_relation,
                          )
                        }
                      >
                        {t('sharing.accessRequestGrant')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pendingRequests.isPending}
                        onClick={() => void pendingRequests.decline(request.id)}
                      >
                        {t('sharing.accessRequestDecline')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Manager visibility info - renders its own separator if it has content */}
        {!isLoadingAccess && ownerId && (
          <ManagerVisibilityInfo ownerId={ownerId} module={module} />
        )}

        <Separator />

        {/* Copy a link to this page. There is no link-based grant — recipients
            still need to be shared in above to open it, so no "anyone with the
            link" claim is made here. */}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" className="shrink-0" onClick={handleCopyLink}>
            <LinkIcon className="h-4 w-4 me-2" />
            {t('sharing.copyLink')}
          </Button>
          {canTransferOwnership && (
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0"
              onClick={() => {
                setTransferTargetId(null);
                setTransferOpen(true);
              }}
            >
              <ArrowLeftRight className="h-4 w-4 me-2" />
              {t('sharing.transferOwnership')}
            </Button>
          )}
        </div>
      </div>
    </ResponsiveDialog>

    <ConfirmDialog
      open={transferOpen}
      onClose={() => {
        setTransferOpen(false);
        setTransferTargetId(null);
      }}
      onConfirm={handleTransferOwnership}
      title={t('sharing.transferOwnership')}
      message={
        currentUserIsOwner
          ? t('sharing.transferOwnershipDescription')
          : t('sharing.transferOwnershipAdminDescription')
      }
      confirmLabel={t('sharing.transferOwnership')}
      isLoading={transferOwnership.isPending}
      destructive={false}
    >
      {/* `_validate_transfer_target` refuses a target that cannot act, so the
          status set here IS its `_TRANSFERABLE_TARGET_STATUSES`. Without it a
          deactivated colleague — exactly who a departing-employee transfer scrolls
          past — was offered and then refused on confirm. */}
      <UserPicker
        value={transferTargetId}
        onChange={setTransferTargetId}
        label={t('sharing.newOwner')}
        placeholder={t('sharing.selectNewOwner')}
        filterUserTypes={['internal']}
        filterUserStatuses={['active', 'pending_setup']}
      />
    </ConfirmDialog>
    </>
  );
}
